// MF-566 — Card 'attendance-report': the "Attendance Report" block of the Belmont dashboard in ONE card — a
// donut of the year-to-date rate, the big number with its caption, three facts (current term, change since
// the previous term, lates) and the term trend underneath. Rows: { period: 'YTD' | 'Term n', value, lates }.
// Nothing is derived beyond arithmetic on what is there: a term without data is a gap, lates are a dash
// until the server sends them, the growth needs two terms with data.
import { useEffect, useMemo, useRef } from 'react'
import ReactECharts from 'echarts-for-react'
import { Box, Typography } from '@mui/material'
import { SUMMARY_PALETTE, FONT, INK, baseTextStyle, fmtValue } from '../boardTheme'

const TRACK = '#dfe3e8'

/** rows → { ytd, current, growth, lates, byTerm } (terms in the order the card option gives). */
export const summarise = (rows = [], terms = []) => {
  const byTerm = new Map(
    rows
      .filter((r) => r.period !== 'YTD' && r.value !== null && r.value !== undefined)
      .map((r) => [r.period, Number(r.value)])
  )
  const have = terms.filter((t) => byTerm.has(t))
  const current = have.length ? byTerm.get(have[have.length - 1]) : null
  const previous = have.length > 1 ? byTerm.get(have[have.length - 2]) : null
  const ytdRow = rows.find((r) => r.period === 'YTD')
  const latesRow = rows.find((r) => r.lates !== null && r.lates !== undefined)
  return {
    ytd: ytdRow && ytdRow.value !== null && ytdRow.value !== undefined ? Number(ytdRow.value) : null,
    current,
    growth: current !== null && previous !== null ? current - previous : null,
    lates: latesRow ? Number(latesRow.lates) : null,
    byTerm
  }
}

const Fact = ({ label, children }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, py: 0.5 }}>
    <Typography sx={{ fontSize: 11, color: SUMMARY_PALETTE.ink }}>{label}</Typography>
    <Typography sx={{ fontSize: 14, color: SUMMARY_PALETTE.good, whiteSpace: 'nowrap' }}>
      {children}
    </Typography>
  </Box>
)

const AttendanceReportCard = ({ data }) => {
  const terms = data?.meta?.card?.options?.terms || []
  const s = useMemo(() => summarise(data?.rows || [], terms), [data, terms])

  const donut = useMemo(
    () => ({
      series: [
        {
          type: 'pie',
          radius: ['62%', '88%'],
          silent: true,
          label: { show: false },
          data: [
            { value: s.ytd ?? 0, itemStyle: { color: SUMMARY_PALETTE.good } },
            { value: Math.max(0, 100 - (s.ytd ?? 0)), itemStyle: { color: TRACK } }
          ]
        }
      ]
    }),
    [s.ytd]
  )

  const trend = useMemo(
    () => ({
      textStyle: baseTextStyle,
      animationDuration: 400,
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v) => (v === null || v === undefined ? '–' : `${fmtValue(v, 1)}%`)
      },
      grid: { top: 12, left: 8, right: 16, bottom: 4, containLabel: true },
      xAxis: {
        type: 'category',
        data: terms,
        axisTick: { show: false },
        axisLine: { lineStyle: { color: INK.axis } },
        axisLabel: { color: INK.secondary, fontSize: 11, fontFamily: FONT }
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        splitNumber: 4,
        splitLine: { lineStyle: { type: 'dashed', color: INK.grid } },
        axisLabel: { color: INK.muted, fontSize: 11 }
      },
      series: [
        {
          type: 'line',
          data: terms.map((t) => (s.byTerm.has(t) ? s.byTerm.get(t) : null)), // a term that has not happened is a gap
          connectNulls: false,
          symbol: 'circle',
          symbolSize: 9,
          lineStyle: { width: 2, color: SUMMARY_PALETTE.good },
          itemStyle: { color: '#fff', borderColor: SUMMARY_PALETTE.good, borderWidth: 2 }
        }
      ]
    }),
    [terms, s.byTerm]
  )

  // ECharts measures its box once: follow the tile when it is resized.
  const ref = useRef(null)
  const box = useRef(null)
  useEffect(() => {
    const el = box.current
    if (!el) return undefined
    const ro = new ResizeObserver(() => ref.current?.getEchartsInstance?.()?.resize())
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const growthColor =
    s.growth === null || s.growth === 0
      ? SUMMARY_PALETTE.faint
      : s.growth > 0
        ? SUMMARY_PALETTE.good
        : SUMMARY_PALETTE.bad

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', minHeight: 0 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 3 }}>
        <Box sx={{ width: 150, height: 150, flexShrink: 0 }}>
          <ReactECharts option={donut} style={{ height: '100%', width: '100%' }} />
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 40, fontWeight: 500, lineHeight: 1, color: SUMMARY_PALETTE.good }}>
              {s.ytd === null ? '–' : `${fmtValue(s.ytd, 0)}%`}
            </Typography>
            <Typography
              sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '.04em', color: SUMMARY_PALETTE.muted }}
            >
              YTD ATTENDANCE RATE
            </Typography>
          </Box>
          <Box sx={{ mt: 1.5, pt: 1, borderTop: `1px solid ${SUMMARY_PALETTE.grid}` }}>
            <Fact label='Current Term Rate'>{s.current === null ? '–' : `${fmtValue(s.current, 0)}%`}</Fact>
            <Fact label='Term to Term Growth'>
              {s.growth === null ? (
                '–'
              ) : (
                <Box component='span' sx={{ color: growthColor }}>
                  {s.growth === 0
                    ? '0 pp'
                    : `${s.growth > 0 ? '▲' : '▼'} ${fmtValue(Math.abs(s.growth), 0)} pp`}
                </Box>
              )}
            </Fact>
            <Fact label='Lates to Class'>{s.lates === null ? '–' : fmtValue(s.lates, 0)}</Fact>
          </Box>
        </Box>
      </Box>
      <div ref={box} style={{ flex: 1, minHeight: 130, minWidth: 0 }}>
        <ReactECharts ref={ref} option={trend} notMerge style={{ height: '100%', width: '100%' }} />
      </div>
    </Box>
  )
}

export default AttendanceReportCard
